import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Plant } from '../types/plants';
import PlantComments from '../components/PlantComments';
import { RecentExperiences } from '../components/RecentExperiences';
import { Comment } from '../components/PlantComments';
import SubmitUpdateForm from '../components/SubmitUpdateForm';
import { API_BASE_URL } from '../config';

const PlantDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const letter = searchParams.get('letter');
  
  console.log('PlantDetail - URL search params:', location.search);
  console.log('PlantDetail - letter param:', letter);
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlant = async () => {
    if (!id) {
      console.log('No plant ID provided');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching plant with ID:', id);
      const response = await axios.get(`${API_BASE_URL}/api/plants/${id}`);
      console.log('Plant data received:', response.data);
      setPlant(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching plant:', err);
      setError('Failed to load plant data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    if (!id) {
      console.log('No plant ID provided for comments');
      return;
    }
    
    try {
      setCommentsLoading(true);
      console.log('Fetching comments for plant ID:', id);
      const response = await axios.get(`${API_BASE_URL}/api/plants/${id}/comments`);
      console.log('Comments received:', response.data);
      
      // Handle both response formats: direct array or { data: [...] }
      const commentsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || []);
      
      setComments(commentsData);
      return commentsData; // Return the comments data for the callback
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
      return [];
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPlant();
      fetchComments();
    }
  }, [id]);

  const renderSection = (title: string, content?: string) => {
    if (!content) return null;
    
    return (
      <div key={title}>
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">{title}</h2>
        <p className="mt-2 text-gray-700 whitespace-pre-line">{content}</p>
      </div>
    );
  };

  console.log('Render - loading:', loading, 'plant:', plant, 'error:', error);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !plant) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {error || 'Plant not found'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get the referring page's search params from the URL or location state
  const getBackLink = () => {
    const searchParams = new URLSearchParams(location.search);
    const letterParam = searchParams.get('letter');
    
    if (letterParam && letterParam !== 'All') {
      return {
        to: { pathname: '/botanicals', search: `?letter=${letterParam}` },
        text: `Back to ${letterParam} Plants`
      };
    } 
    
    if (location.state?.from === 'botanicals' && location.state?.search) {
      const fromSearch = new URLSearchParams(location.state.search);
      const fromLetter = fromSearch.get('letter');
      
      if (fromLetter && fromLetter !== 'All') {
        return {
          to: { pathname: '/botanicals', search: `?letter=${fromLetter}` },
          text: `Back to ${fromLetter} Plants`
        };
      }
    }
    
    // Default back to all plants
    return {
      to: '/botanicals',
      text: 'Back to All Plants'
    };
  };
  
  const backLink = getBackLink();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-4">
          <Link 
            to={backLink.to}
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {backLink.text}
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="md:flex">
            {/* Left Column - Image and Sidebar */}
            <div className="md:w-1/3 bg-gray-50 p-6">
              {plant.imageUrl ? (
                <div className="relative w-full h-64 overflow-hidden rounded-lg">
                  <img
                    src={plant.imageUrl.replace('unsplash.com/photos/', 'images.unsplash.com/photo-')}
                    alt={Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names || plant.latin_name || 'Plant image'}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 hidden">
                    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400">
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900">Quick Info</h3>
                <dl className="mt-2 space-y-2">
                  {plant.family && (
                    <div className="flex">
                      <dt className="text-sm font-medium text-gray-500 w-24">Family:</dt>
                      <dd className="text-sm text-gray-900">{plant.family}</dd>
                    </div>
                  )}
                  {plant.latin_name && (
                    <div className="flex">
                      <dt className="text-sm font-medium text-gray-500 w-24">Latin Name:</dt>
                      <dd className="text-sm text-gray-900 italic">{plant.latin_name}</dd>
                    </div>
                  )}
                  {plant.medicinal_properties && plant.medicinal_properties.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {plant.medicinal_properties.map((property, index) => (
                        <span key={index} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {property}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {plant.preparation_methods && plant.preparation_methods.length > 0 && (
                    <>
                      <hr className="my-3 border-gray-200" />
                      <div className="mt-2">
                        <dt className="text-sm font-medium text-gray-500 mb-1">Preparation Methods:</dt>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {plant.preparation_methods.map((method, index) => (
                            <span key={index} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                              {method}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {plant.range && (
                    <>
                      <hr className="my-3 border-gray-200" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Native Range:</dt>
                        <dd className="text-sm text-gray-900 mt-1">{plant.range}</dd>
                      </div>
                    </>
                  )}
                  
                  {plant.known_hazards && (
                    <>
                      <hr className="my-3 border-gray-200" />
                      <div className="p-3 bg-red-50 rounded-lg">
                        <dt className="text-sm font-medium text-red-700">⚠️ Hazards:</dt>
                        <dd className="text-sm text-red-600 mt-1">{plant.known_hazards}</dd>
                      </div>
                    </>
                  )}
                </dl>
              </div>
              
              {/* Recent Experiences */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <RecentExperiences
                  comments={comments}
                  loading={commentsLoading}
                  plantId={id || ''}
                />
              </div>
              
              {/* Submit Update Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Submit an Update</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Notice something that needs updating? Let us know!
                </p>
                <SubmitUpdateForm 
                  plantId={id || ''} 
                  plantName={Array.isArray(plant.common_names) ? plant.common_names[0] : plant.common_names || 'this plant'} 
                />
              </div>
            </div>
            
            {/* Right Column - Details */}
            <div className="md:w-2/3 p-8">
              <div className="flex flex-col">
                <h1 className="text-3xl font-bold text-gray-900">
                  {typeof plant.common_names === 'string' ? plant.common_names : plant.common_names?.[0] || plant.latin_name}
                </h1>
                {plant.latin_name && (
                  <p className="text-lg text-gray-600">{plant.latin_name}</p>
                )}
              </div>
              
              <div className="mt-6 space-y-6">
                {plant.summary && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Summary</h2>
                    <p className="mt-2 text-gray-700">{plant.summary}</p>
                  </div>
                )}
                
                {renderSection('Physical Characteristics', plant.physical_characteristics)}
                {renderSection('Medicinal Uses', plant.medicinal_uses)}
                {renderSection('Edible Uses', plant.edible_uses)}
                {renderSection('Other Uses', plant.other_uses)}
                {renderSection('Cultivation', plant.cultivation)}
                {renderSection('Propagation', plant.propagation)}
                
                {plant.habitats && (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Habitats</h2>
                    <p className="mt-2 text-gray-700">{plant.habitats}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Comments Section */}
          <div className="mt-12 px-6 pb-8">
            <PlantComments 
              plantId={id || ''} 
              onCommentAdded={fetchComments} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlantDetail;
